import { Post } from '../types';
import React, { createContext, useContext, useEffect, useLayoutEffect, useRef, useCallback, useMemo, useState } from 'react';
import {
  useMasonry,
  usePositioner,
  useResizeObserver,
  useContainerPosition,
} from 'masonic';
import type { RenderComponentProps } from 'masonic';
import { Loader } from '@/components/ui/loader';
import { ReelCard } from './ReelCard';
import ReelCardErrorBoundary from './ReelCardErrorBoundary';
import ListView from './ListView';
import { Category } from '@/services/api';

interface ReelGridProps {
  reels: Post[];
  loading: boolean;
  initialLoading: boolean;
  isBlurred: boolean;
  onLoadMore: () => void;
  hasMore: boolean;
  viewMode?: 'grid' | 'list';
  onNoteUpdate?: (postId: number, note: string) => void;
  onCategoryUpdate?: (postId: number, category?: any) => void;
  onPostDelete?: (postId: number) => void;
  onPostArchive?: (postId: number) => void;
  favouriteStates?: { [key: string]: boolean };
  onFavouriteToggle?: (postId: number, isFavourite: boolean) => void;
  categories: Category[];
  selectionMode?: boolean;
  selectedPostIds?: Set<number>;
  onToggleSelect?: (postId: number, shiftKey?: boolean) => void;
  // The ancestor pane that actually scrolls (the page/window no longer does — see
  // useElementScroll below). Falls back to window scroll if omitted.
  scrollContainerRef?: React.RefObject<HTMLElement>;
}

// Minimum column width: narrower columns on small screens, more columns on wide.
// ~300px per column + 16 gutter → 2 cols ~616px, 3 cols ~932px, 4 cols ~1248px, 5 cols ~1564px
const COLUMN_WIDTH = 300;
const COLUMN_GUTTER = 16;
const ROW_GUTTER = 16;
const ITEM_HEIGHT_ESTIMATE = 400;

// masonic's <Masonry> only ever tracks `window` scroll position (see its useScroller/
// MasonryScroller implementation). Our layout now scrolls posts inside their own pane
// (`scrollContainerRef`), not the window, so masonic would always compute scrollTop as
// ~0 and only ever render the first screenful of items — this hook replicates masonic's
// own scroll-tracking math, but bound to that pane (or `window` if none is given).
function useElementScroll(
  scrollContainerRef: React.RefObject<HTMLElement> | undefined,
  gridRef: React.RefObject<HTMLElement>,
  // The grid container is only mounted once `initialLoading` flips off, so a plain ref
  // dependency (stable identity, never changes) would make this effect run once too
  // early — while `gridRef.current` is still null — and never again. `gridEl` is state
  // that changes when the callback ref actually attaches, forcing a proper re-run.
  gridEl: HTMLElement | null,
  fps = 12
) {
  const [state, setState] = useState({ scrollTop: 0, isScrolling: false, height: 0 });

  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;

    const useWindow = !scrollContainerRef?.current;
    const scrollEl = scrollContainerRef?.current ?? null;

    let raf = 0;
    let scrollTimeout: ReturnType<typeof setTimeout>;

    const measure = () => {
      const gridRect = grid.getBoundingClientRect();
      const viewportTop = useWindow ? 0 : (scrollEl as HTMLElement).getBoundingClientRect().top;
      const currentScroll = useWindow ? window.scrollY : (scrollEl as HTMLElement).scrollTop;
      const height = useWindow ? window.innerHeight : (scrollEl as HTMLElement).clientHeight;
      // Static distance from the grid's top to the scroll pane's top, independent of
      // however much has been scrolled at measurement time.
      const offset = gridRect.top - viewportTop + currentScroll;
      const scrollTop = Math.max(0, currentScroll - offset);
      setState((prev) => (prev.scrollTop === scrollTop && prev.height === height ? prev : { ...prev, scrollTop, height }));
    };

    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
      setState((prev) => (prev.isScrolling ? prev : { ...prev, isScrolling: true }));
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => setState((prev) => ({ ...prev, isScrolling: false })), 40 + 1000 / fps);
    };

    measure();
    const target: HTMLElement | Window = useWindow ? window : (scrollEl as HTMLElement);
    target.addEventListener('scroll', onScroll, { passive: true });

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(grid);
    if (!useWindow) resizeObserver.observe(scrollEl as HTMLElement);
    window.addEventListener('resize', measure);

    return () => {
      target.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', measure);
      resizeObserver.disconnect();
      cancelAnimationFrame(raf);
      clearTimeout(scrollTimeout);
    };
  }, [scrollContainerRef, gridRef, gridEl, fps]);

  return state;
}

interface ReelCardContextValue {
  onNoteUpdate?: (postId: number, note: string) => void;
  onCategoryUpdate?: (postId: number, category?: any) => void;
  onPostDelete?: (postId: number) => void;
  onPostArchive?: (postId: number) => void;
  favouriteStates?: { [key: string]: boolean };
  onFavouriteToggle?: (postId: number, isFavourite: boolean) => void;
  categories: Category[];
  selectionMode?: boolean;
  selectedPostIds?: Set<number>;
  onToggleSelect?: (postId: number, shiftKey?: boolean) => void;
}

// Everything a card needs EXCEPT its own `data` travels by context, not through the
// masonic `render` closure. That keeps the `render` function's identity stable (see
// `renderCard` below) so masonic never remounts cells when selection/handlers/categories
// change — those updates instead re-render the ReelCards in place via context. `data` is
// the only per-cell prop and comes straight from masonic. This is what stops the
// select-one-post → remount-all → flicker (visible on no-media cards) behaviour.
const ReelCardContext = createContext<ReelCardContextValue | null>(null);

const MasonryCard = ({ data, width }: RenderComponentProps<Post>) => {
  const ctx = useContext(ReelCardContext);
  if (!data?.id || !ctx) return null;
  return (
  <div style={{ width }} className="w-full">
    <ReelCardErrorBoundary reelId={String(data.id)}>
      <ReelCard
        reel={data}
        onNoteUpdate={ctx.onNoteUpdate}
        onCategoryUpdate={ctx.onCategoryUpdate}
        onPostDelete={ctx.onPostDelete}
        onPostArchive={ctx.onPostArchive}
        favouriteStates={ctx.favouriteStates}
        onFavouriteToggle={ctx.onFavouriteToggle}
        categories={ctx.categories}
        selectionMode={ctx.selectionMode}
        isSelected={!!ctx.selectedPostIds?.has(data.id)}
        onToggleSelect={ctx.onToggleSelect}
      />
    </ReelCardErrorBoundary>
  </div>
  );
};

const ReelGrid: React.FC<ReelGridProps> = ({
  reels,
  loading,
  initialLoading,
  isBlurred,
  onLoadMore,
  hasMore,
  viewMode = 'grid',
  onNoteUpdate,
  onCategoryUpdate,
  onPostDelete,
  onPostArchive,
  favouriteStates,
  onFavouriteToggle,
  categories,
  selectionMode,
  selectedPostIds,
  onToggleSelect,
  scrollContainerRef,
}) => {
  const observer = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const loadingRef = useRef(loading);
  const hasMoreRef = useRef(hasMore);
  const gridContainerRef = useRef<HTMLDivElement | null>(null);
  // The grid container only mounts once `initialLoading` is false, so we need to know
  // exactly when that DOM node first attaches (a plain ref doesn't trigger a re-render
  // or notify any effect when `.current` changes) — this callback ref + state gives us that.
  const [gridEl, setGridEl] = useState<HTMLDivElement | null>(null);
  const setGridContainerRef = useCallback((node: HTMLDivElement | null) => {
    gridContainerRef.current = node;
    setGridEl(node);
  }, []);

  // Only pass valid Post objects so masonic never sees undefined (WeakMap key error).
  // Stable reference so length changes don't race with the positioner.
  const validReels = useMemo(
    () => reels.filter((r): r is Post => r != null && typeof r === 'object' && typeof (r as Post).id !== 'undefined'),
    [reels]
  );

  // masonic's positioner caches cell positions by index in an interval tree and assumes
  // `items` only ever grows (appends). When the list SHRINKS (bulk delete/archive,
  // unfavourite in favourites view, category/search/filter switch), the tree still holds
  // stale indices past the new length; masonic then reads items[staleIndex] === undefined
  // and feeds it to a WeakMap-keyed memo → "Invalid value used as weak map key" crash.
  // Bumping a layout epoch on shrink forces a fresh positioner. It must happen in render
  // phase (not an effect) so the new positioner is in place the same render the shorter
  // list arrives — an effect would run after the crashing render. Appends don't bump the
  // epoch, so infinite scroll keeps its measured layout with no reflow.
  const prevReelCountRef = useRef(validReels.length);
  const layoutEpochRef = useRef(0);
  if (validReels.length < prevReelCountRef.current) {
    layoutEpochRef.current += 1;
  }
  prevReelCountRef.current = validReels.length;

  const { scrollTop, isScrolling, height } = useElementScroll(scrollContainerRef, gridContainerRef, gridEl);
  const { width: rawWidth } = useContainerPosition(gridContainerRef, [height, gridEl]);
  // Round to whole px: sub-pixel width jitter (e.g. scrollbar gutter, fractional layout)
  // would otherwise recreate the positioner every render → masonic remounts every cell →
  // each card's image state resets and refetches. Rounding pins a stable positioner.
  const width = Math.round(rawWidth);
  const positioner = usePositioner(
    {
      width,
      columnWidth: COLUMN_WIDTH,
      columnGutter: COLUMN_GUTTER,
      rowGutter: ROW_GUTTER,
      maxColumnCount: 7,
    },
    [width, layoutEpochRef.current]
  );
  const resizeObserver = useResizeObserver(positioner);

  // masonic memoizes each cell's element by the identity of this `render` function
  // (trie key [RenderComponent, index, data, columnWidth] in use-masonry.js). Any change
  // in identity → memo miss → masonic emits a different component type per cell → React
  // unmounts+remounts every card (wiping MediaRenderer image state and flickering the UI).
  // So `renderCard` must have a CONSTANT identity: empty deps, and it reads nothing that
  // changes — the only per-cell input is `props` (data/width) from masonic. All the mutable
  // stuff (handlers, categories, selection) reaches cards via ReelCardContext below, which
  // re-renders them in place on change instead of remounting.
  const renderCard = useCallback(
    (props: RenderComponentProps<Post>) => <MasonryCard {...props} />,
    []
  );

  // Context value carrying everything except per-cell `data`. Memoized so it only changes
  // identity when one of these actually changes (e.g. a selection toggle) — that propagates
  // to the ReelCards as an in-place re-render, never a remount.
  const cardContextValue = useMemo<ReelCardContextValue>(
    () => ({ onNoteUpdate, onCategoryUpdate, onPostDelete, onPostArchive, favouriteStates, onFavouriteToggle, categories, selectionMode, selectedPostIds, onToggleSelect }),
    [onNoteUpdate, onCategoryUpdate, onPostDelete, onPostArchive, favouriteStates, onFavouriteToggle, categories, selectionMode, selectedPostIds, onToggleSelect]
  );

  // Hooks must run unconditionally on every render (including while `initialLoading` or
  // `viewMode === 'list'` skip using this element), so this is called up-front and only
  // the resulting element is used conditionally down in the JSX below.
  const masonryGrid = useMasonry({
    positioner,
    resizeObserver,
    items: validReels,
    height: height || 1,
    scrollTop,
    isScrolling,
    containerRef: setGridContainerRef,
    itemHeightEstimate: ITEM_HEIGHT_ESTIMATE,
    itemKey: (data, index) => (data && typeof data.id !== 'undefined' ? data.id : `item-${index}`),
    overscanBy: 2,
    render: renderCard,
  });

  useEffect(() => {
    loadingRef.current = loading;
    hasMoreRef.current = hasMore;
  }, [loading, hasMore]);

  const lastReelElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observer.current) observer.current.disconnect();

      if (loading || isBlurred) return;

      if (hasMore) {
        observer.current = new IntersectionObserver(
          (entries) => {
            if (
              entries[0].isIntersecting &&
              hasMoreRef.current &&
              !loadingRef.current
            ) {
              onLoadMore();
            }
          },
          { threshold: 0.5 }
        );

        if (node) observer.current.observe(node);
      }
    },
    [onLoadMore, isBlurred, loading, hasMore]
  );

  useEffect(() => {
    return () => {
      if (observer.current) observer.current.disconnect();
    };
  }, []);

  if (viewMode === 'list') {
    return (
      <ListView
        reels={reels}
        loading={loading}
        initialLoading={initialLoading}
        isBlurred={isBlurred}
        onLoadMore={onLoadMore}
        hasMore={hasMore}
        onNoteUpdate={onNoteUpdate}
        onCategoryUpdate={onCategoryUpdate}
        onPostDelete={onPostDelete}
        onPostArchive={onPostArchive}
        favouriteStates={favouriteStates}
        onFavouriteToggle={onFavouriteToggle}
        categories={categories}
        selectionMode={selectionMode}
        selectedPostIds={selectedPostIds}
        onToggleSelect={onToggleSelect}
      />
    );
  }

  return (
    <ReelCardContext.Provider value={cardContextValue}>
      <div className="text-white px-2 relative">
        {initialLoading ? (
          <div className="flex justify-center items-center h-[calc(100vh-200px)]">
            <Loader size="lg" showText text="Loading your saves..." />
          </div>
        ) : (
          <div className="max-w-[2200px] mx-auto w-full">
            <div className="mt-4">
              {masonryGrid}
            </div>

            {/* Sentinel for infinite scroll: when this is visible, load more */}
            <div
              ref={lastReelElementRef}
              className="min-h-[1px] w-full"
              aria-hidden
            />

            <div ref={loadMoreRef} className="py-4 flex justify-center">
              {loading && !initialLoading && (
                <Loader size="md" showText text="Loading more reels..." />
              )}
            </div>
          </div>
        )}
        {isBlurred && !initialLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-0 backdrop-blur-md z-1" />
        )}
      </div>
    </ReelCardContext.Provider>
  );
};

export default ReelGrid;
